// PGN, modern subset. Export writes the Seven Tag Roster plus SetUp/FEN for
// games that start from a custom position. Import reads the first game's
// mainline and skips comments, NAGs and variations rather than rejecting them.
import { GameState } from './GameState.js';
import { START_FEN } from './fen.js';
import { moveToSan, sanToMove } from './san.js';
import type { Outcome } from './outcome.js';
import { FIDE, withReplayLimits, type RuleSet } from './rules/index.js';

export type PgnResult = '1-0' | '0-1' | '1/2-1/2' | '*';

export type PgnHeaders = Readonly<Record<string, string>>;

const SEVEN_TAG_ROSTER = ['Event', 'Site', 'Date', 'Round', 'White', 'Black', 'Result'] as const;
const UNKNOWN_TAG = '?';
const LINE_WIDTH = 80;

export function pgnResult(outcome: Outcome | null): PgnResult {
  if (outcome === null) return '*';
  if (outcome.winner === null) return '1/2-1/2';
  return outcome.winner === 'w' ? '1-0' : '0-1';
}

function escapeTag(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function wrap(tokens: readonly string[]): string {
  const lines: string[] = [];
  let line = '';
  for (const token of tokens) {
    if (line !== '' && line.length + 1 + token.length > LINE_WIDTH) {
      lines.push(line);
      line = token;
    } else {
      line = line === '' ? token : `${line} ${token}`;
    }
  }
  if (line !== '') lines.push(line);
  return lines.join('\n');
}

/** One ply of a finished or running game: its SAN and the position it was played from. */
export interface SanPly {
  readonly san: string;
  readonly turn: 'w' | 'b';
  readonly fullmoveNumber: number;
}

/** Every move of a game in SAN, in order. */
export function sanPlies(state: GameState): SanPly[] {
  const plies: SanPly[] = [];
  // The moves were already played: replay them under the automatic limits, not live-play draws.
  let replay = GameState.fromFen(state.startFen, withReplayLimits(state.rules));
  for (const move of state.moves) {
    const pos = replay.position;
    plies.push({ san: moveToSan(pos, move, replay.legalMoves), turn: pos.turn, fullmoveNumber: pos.fullmoveNumber });
    replay = replay.play(move);
  }
  return plies;
}

/**
 * Movetext tokens with move numbers: "1." "e4" "e5" "2." … ("1..." when Black
 * starts, and again after a comment, as the standard asks).
 */
function movetext(state: GameState, comments: readonly (string | null)[]): string[] {
  const tokens: string[] = [];
  let afterComment = false;
  sanPlies(state).forEach((ply, i) => {
    if (ply.turn === 'w') tokens.push(`${ply.fullmoveNumber}.`);
    else if (tokens.length === 0 || afterComment) tokens.push(`${ply.fullmoveNumber}...`);
    tokens.push(ply.san);
    const comment = comments[i] ?? null;
    afterComment = comment !== null;
    if (comment !== null) tokens.push(`{${comment.replace(/[{}]/g, '')}}`);
  });
  return tokens;
}

/**
 * Serialise a game. Missing roster tags are written as "?"; Result always
 * follows the game. `comments[i]` is written after move `i` (clock readings).
 */
export function toPgn(state: GameState, headers: PgnHeaders = {}, comments: readonly (string | null)[] = []): string {
  const result = pgnResult(state.outcome);
  const tags: [string, string][] = SEVEN_TAG_ROSTER.map((name) => [name, name === 'Result' ? result : headers[name] ?? UNKNOWN_TAG]);
  for (const [name, value] of Object.entries(headers)) {
    if (!(SEVEN_TAG_ROSTER as readonly string[]).includes(name) && name !== 'SetUp' && name !== 'FEN') tags.push([name, value]);
  }
  if (state.startFen !== START_FEN) tags.push(['SetUp', '1'], ['FEN', state.startFen]);
  const header = tags.map(([name, value]) => `[${name} "${escapeTag(value)}"]`).join('\n');
  return `${header}\n\n${wrap([...movetext(state, comments), result])}\n`;
}

export interface PgnGame {
  readonly headers: PgnHeaders;
  readonly state: GameState;
  readonly result: PgnResult;
}

export type PgnParseResult = { readonly ok: true; readonly game: PgnGame } | { readonly ok: false; readonly error: string };

const TAG_LINE = /^\s*\[(\w+)\s+"((?:[^"\\]|\\.)*)"\s*\]\s*$/;
const RESULT_TOKENS: readonly PgnResult[] = ['1-0', '0-1', '1/2-1/2', '*'];

function isPgnResult(token: string): token is PgnResult {
  return (RESULT_TOKENS as readonly string[]).includes(token);
}

function stripNonMainline(text: string): string {
  let out = '';
  let depth = 0;
  let inComment = false;
  for (const line of text.split('\n')) {
    const lineText = line.trimStart().startsWith('%') ? '' : line;
    for (let i = 0; i < lineText.length; i++) {
      const ch = lineText.charAt(i);
      if (inComment) { if (ch === '}') inComment = false; continue; }
      if (ch === '{') { inComment = true; continue; }
      if (ch === ';' && depth === 0) break;
      if (ch === '(') { depth++; continue; }
      if (ch === ')') { depth = Math.max(0, depth - 1); continue; }
      if (depth === 0) out += ch;
    }
    out += '\n';
  }
  return out;
}

/** Parse a PGN. Replays under FIDE's automatic draw limits (see `withReplayLimits`). */
export function parsePgn(text: string, rules: RuleSet = FIDE): PgnParseResult {
  const headers: Record<string, string> = {};
  const body: string[] = [];
  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    const tag = TAG_LINE.exec(line);
    if (tag !== null && body.every((l) => l.trim() === '')) headers[tag[1] ?? ''] = (tag[2] ?? '').replace(/\\(.)/g, '$1');
    else body.push(line);
  }

  const startFen = headers['FEN'] ?? START_FEN;
  const initial = GameState.tryFromFen(startFen, withReplayLimits(rules));
  if (typeof initial === 'string') return { ok: false, error: `Invalid FEN tag: ${initial}` };

  let state = initial;
  let result: PgnResult = '*';
  const tokens = stripNonMainline(body.join('\n')).split(/\s+/).filter((t) => t !== '');
  for (const raw of tokens) {
    if (isPgnResult(raw)) { result = raw; break; }
    const token = raw.replace(/^\d+\.(\.\.)?/, '');
    if (token === '' || token.startsWith('$')) continue;
    const move = sanToMove(state.position, token, state.legalMoves);
    if (move === undefined) return { ok: false, error: `Illegal or unreadable move "${raw}" at ply ${state.moves.length + 1}` };
    state = state.play(move);
  }
  // Play on from an import under the caller's rules, not the relaxed replay limits.
  return { ok: true, game: { headers, state: state.withRules(rules), result } };
}
